import GuideLayout, {
  H2,
  H3,
  P,
  UL,
  OL,
  Callout,
  ExternalLink,
  Strong,
  Em,
} from "../ui/GuideLayout.jsx";
import { Link } from "react-router-dom";

export default function KakuteiShinkoku() {
  return (
    <GuideLayout
      title="Como fazer kakutei shinkoku"
      subtitle="O ajuste anual de imposto de renda no Japão, explicado em português sem termos confusos."
      slug="kakutei-shinkoku"
      description="Guia prático sobre o kakutei shinkoku (確定申告), o ajuste anual de imposto de renda no Japão. Quem precisa fazer, quando, como, deduções comuns."
      updated="Atualizado anualmente em fevereiro"
      readTime="10 min"
    >
      <P>
        <Em>Kakutei shinkoku</Em> (確定申告) é o ajuste anual de imposto de
        renda no Japão. É o equivalente japonês da declaração de IR brasileira:
        você reporta sua renda total do ano anterior e o sistema calcula se
        você pagou imposto a mais (e tem direito a restituição) ou a menos (e
        precisa pagar a diferença).
      </P>
      <P>
        A boa notícia: <Strong>muita gente que devia fazer não faz</Strong> e
        está deixando dinheiro na mesa. Este guia explica quem precisa, quem
        deveria, e como funciona.
      </P>

      <H2 id="quem-precisa">Quem precisa fazer kakutei shinkoku</H2>
      <P>
        Você é <Strong>obrigado</Strong> a declarar se algum desses casos se
        aplica a você:
      </P>
      <UL>
        <li>
          Sua renda anual passou de <Strong>¥20 milhões</Strong>.
        </li>
        <li>
          Você teve renda de <Strong>2 ou mais empregos</Strong> e o emprego
          secundário pagou mais de ¥200.000/ano.
        </li>
        <li>
          Você teve renda extra além do salário (autônomo, freelance, aluguel,
          investimentos, criptos) acima de <Strong>¥200.000/ano</Strong>.
        </li>
        <li>
          Você é <Strong>autônomo</Strong> ou pequeno empresário individual.
        </li>
        <li>
          Você recebeu indenização de seguro de saúde acima do valor pago em
          tratamentos.
        </li>
        <li>
          Sua empresa não fez o <Em>nenmatsu chosei</Em> (年末調整 — o ajuste
          de fim de ano interno).
        </li>
      </UL>

      <P>
        E você <Em>pode escolher</Em> fazer (mesmo sem obrigação) se quer
        reclamar deduções que a empresa não considerou. Esses são os casos em
        que você normalmente <Strong>recebe restituição</Strong>:
      </P>
      <UL>
        <li>
          Gastos médicos seus ou de família acima de <Strong>¥100.000</Strong> no
          ano (ou 5% da renda, o que for menor) — chama <Em>iryouhi kojo</Em>.
        </li>
        <li>
          Doação por <Strong>furusato nozei</Strong> (ふるさと納税) — você
          praticamente sempre tem direito.
        </li>
        <li>
          Contribuições para <Strong>iDeCo</Strong> (aposentadoria privada).
        </li>
        <li>
          Compra ou reforma de casa própria (住宅ローン控除 — dedução de
          financiamento residencial).
        </li>
        <li>
          Mudança de emprego no meio do ano (a empresa nova pode não ter feito
          o ajuste completo).
        </li>
        <li>
          Você saiu de um emprego no ano e ainda não trabalhou em outro.
        </li>
      </UL>

      <Callout>
        <Strong>Se você é dekassegui típico</Strong> (CLT japonês, um único
        empregador, sem renda extra): provavelmente <Em>não é obrigado</Em>,
        mas pode valer a pena fazer se teve gasto médico alto, fez furusato
        nozei, ou contribui pra iDeCo.
      </Callout>

      <H2 id="quando-fazer">Quando fazer</H2>
      <P>
        O período de declaração é tipicamente <Strong>de 16 de fevereiro a 15
        de março</Strong> de cada ano, referente ao ano-calendário anterior
        (janeiro a dezembro). Se 15 de março cai em fim de semana, estende
        para a próxima segunda-feira.
      </P>
      <P>
        Confirme as datas exatas no site oficial da National Tax Agency:{" "}
        <ExternalLink href="https://www.nta.go.jp/taxes/shiraberu/shinkoku/kakutei.htm">
          nta.go.jp/taxes/shiraberu/shinkoku/kakutei.htm
        </ExternalLink>
      </P>
      <Callout kind="warn">
        Se você está <Strong>solicitando apenas restituição</Strong> (não tem
        obrigação de declarar), pode fazer fora do prazo regular — você tem 5
        anos retroativos para pedir devolução de imposto pago a mais.
      </Callout>

      <H2 id="documentos">Documentos necessários</H2>
      <UL>
        <li>
          <Strong>Gensen choushuhyo</Strong> (源泉徴収票) — o "informe de
          rendimentos" emitido pela empresa. Pegue cópia em janeiro.
        </li>
        <li>
          <Strong>Comprovantes de gastos dedutíveis:</Strong> recibos médicos,
          comprovantes de furusato nozei, comprovantes de iDeCo, juros de
          financiamento residencial.
        </li>
        <li>
          <Strong>My Number</Strong> (mynumber card ou notification card).
        </li>
        <li>
          <Strong>Dados bancários</Strong> para receber a restituição (nome do
          banco, agência, conta — em seu nome).
        </li>
        <li>
          <Strong>Inkan</Strong> (carimbo pessoal) — se for declarar
          presencialmente.
        </li>
      </UL>

      <H2 id="como-fazer">Como fazer — três caminhos</H2>

      <H3>1. Online via e-Tax (mais rápido)</H3>
      <P>
        Use o site oficial da NTA:{" "}
        <ExternalLink href="https://www.keisan.nta.go.jp/">
          keisan.nta.go.jp
        </ExternalLink>{" "}
        — funciona em japonês. Preenche tudo no navegador, envia digitalmente.
      </P>
      <P>Você precisa de:</P>
      <UL>
        <li>My Number card com chip funcionando.</li>
        <li>Leitor de cartão (¥3.000 numa loja de eletrônicos) <Em>ou</Em> celular com NFC e o app oficial.</li>
        <li>Paciência para navegar no japonês.</li>
      </UL>

      <H3>2. Presencialmente na receita (税務署 / zeimusho)</H3>
      <P>
        Vá ao zeimusho do seu bairro com todos os documentos. Eles ajudam você
        a preencher. <Strong>Vá cedo</Strong> — depois das 10h da manhã na alta
        temporada (fevereiro/março), a fila pode passar de 3 horas.
      </P>
      <P>
        Atendimento em japonês. Algumas prefeituras com forte presença
        brasileira (Aichi, Shizuoka, Mie, Gunma) têm dias com intérprete em
        português — ligue antes pra confirmar.
      </P>

      <H3>3. Com um contador (税理士 / zeirishi)</H3>
      <P>
        Custo típico: ¥20.000 a ¥50.000 para casos simples (um empregador,
        algumas deduções). Mais caro se tiver renda complexa.
      </P>
      <P>
        <Strong>Vale a pena</Strong> se: você tem múltiplas fontes de renda,
        está pegando o dattai ichijikin e precisa de representante fiscal, ou
        tem dúvida sobre deduções específicas.
      </P>

      <H2 id="furusato-nozei">Furusato nozei: a dedução mais subestimada</H2>
      <P>
        <Em>Furusato nozei</Em> (ふるさと納税) deixa você redirecionar parte do
        seu imposto residencial pra qualquer município japonês de sua escolha,
        e em troca o município te manda um &ldquo;agradecimento&rdquo; — geralmente
        comida, carne, frutas, arroz, saquê, eletrodoméstico, etc.
      </P>
      <P>
        Como funciona: você &ldquo;doa&rdquo; via site (rakuten furusato, satofuru,
        furusato choice) e pode descontar quase 100% no imposto. O custo
        efetivo é apenas ¥2.000 por ano, independente de quanto você doou.
      </P>
      <P>
        <Strong>Quanto pode doar:</Strong> depende do seu salário. Em geral
        entre 1% e 3% da renda anual. Os sites têm calculadora — use sempre
        antes de doar.
      </P>
      <Callout>
        Se você ganha ¥4 milhões/ano, pode doar cerca de ¥40.000 a ¥50.000 e
        receber em produtos. Custo real: ¥2.000. É praticamente comida grátis
        por um ano.
      </Callout>

      <H2 id="erros-comuns">Erros comuns</H2>
      <UL>
        <li>
          <Strong>Esquecer de declarar gasto médico</Strong> que passou de
          ¥100.000 no ano. Some hospital, dentista, óculos, transporte para
          tratamento. Inclui família dependente.
        </li>
        <li>
          <Strong>Não declarar trabalho extra</Strong> (arubaito de fim de
          semana) — a empresa principal não sabe, mas a receita cruza os dados.
          Melhor declarar.
        </li>
        <li>
          <Strong>Achar que furusato nozei foi automática</Strong> — se você
          fez via &ldquo;one-stop system&rdquo; (ワンストップ), não precisa
          declarar; se não fez ou fez kakutei shinkoku por outro motivo,
          precisa incluir no kakutei.
        </li>
        <li>
          <Strong>Perder o prazo de 5 anos retroativos.</Strong> Se você
          descobre hoje que tinha direito a restituição em 2021, pode pedir
          até dezembro de 2026 (5 anos do final do ano fiscal).
        </li>
      </UL>

      <H2 id="depois">Depois de declarar</H2>
      <P>
        Se tem restituição: cai na conta em <Strong>4 a 6 semanas</Strong>{" "}
        (e-Tax) ou <Strong>1 a 2 meses</Strong> (presencial).
      </P>
      <P>
        Se tem imposto a pagar: você pode pagar à vista (no banco, conbini, ou
        débito direto) ou parcelar em 2 vezes (até final de maio o restante).
      </P>

      <H2 id="conclusao">Resumindo</H2>
      <OL>
        <li>
          Veja se você se enquadra em obrigação ou direito a restituição.
        </li>
        <li>
          Junte gensen choushuhyo + comprovantes de deduções em janeiro.
        </li>
        <li>
          Faça entre 16/fev e 15/mar, online ou presencial.
        </li>
        <li>
          Se for caso complexo, considere contador.
        </li>
        <li>
          Em caso de dúvida real, ligue para o zeimusho — eles atendem (em
          japonês).
        </li>
      </OL>
      <P>
        Fonte oficial:{" "}
        <ExternalLink href="https://www.nta.go.jp/">
          National Tax Agency (nta.go.jp)
        </ExternalLink>
        . Quando há mudança nas regras, é lá que sai primeiro.
      </P>
    </GuideLayout>
  );
}
